/**
 * NR-5b: connectivity detection hook for the network-resilience UX.
 *
 * Detection is MEASUREMENT-BASED — driven primarily by actual server
 * reachability via ping → pong round-trips. It does NOT trust `navigator.onLine`
 * as a POSITIVE signal (onLine reports "online" with any NIC) nor
 * `NetworkInformation.effectiveType` (reports e.g. "3g" on a fast localhost
 * link). It DOES use `navigator.onLine === false` as a reliable NEGATIVE signal:
 * if the browser says offline there is genuinely no network, so it overrides the
 * "OpenAI socket looks OPEN" suppression (that readyState is a stale local flag
 * on a real drop). Quality is graded from the MEASURED round-trip time:
 *
 *   stable       fast pongs (RTT ≤ unstableRttMs)            → ACTIVE
 *   unstable     slow-ish pongs (unstableRttMs<RTT≤poorRttMs) or a single missed ping
 *   poor         very slow pongs (RTT > poorRttMs)           → DEGRADED (poor)
 *   reconnecting >missLimit consecutive pings unanswered, or a reliable `offline`
 *                event. RECOVERY only on a real pong.
 *
 * The `offline` event is a fast NEGATIVE hint only; `online` never resumes (it
 * only triggers an immediate probe). GATED: disabled → inert.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { connectionReducer, getReconnectCountdown, isConnectionLostOnBrowserOfflineEnabled, isConnectionLostRequiresSocketDownEnabled, isHardMissRequiresSocketDownEnabled, isJoinMonitorArmAfterAiEnabled, isNetqSmoothingEnabled, } from './connection-machine';
/**
 * WS-1: grade the connection quality from a WINDOW of recent RTT samples with
 * asymmetric hysteresis. Pure/testable.
 *
 *   - Enter "poor"/"unstable" only when at least `degradeSamples` of the window
 *     exceed the respective threshold (robust to a single slow AI-turn pong).
 *   - Recover to stable (null) only after the last `recoverSamples` samples are
 *     all fast (≤ unstable), so the pill can't strobe on one fast pong.
 *   - Otherwise HOLD the current quality (the hysteresis band).
 */
export function gradeQualityFromWindow(window, current, cfg) {
    // Recovery takes precedence: the last `recoverSamples` samples being all fast
    // is a stronger, more recent signal than older slow samples still in the
    // window. The caller clears the window on recovery so a stale slow sample
    // can't immediately re-trip the degrade counts (which would re-introduce the
    // flapping this smoothing exists to prevent).
    const tail = window.slice(-cfg.recoverSamples);
    const recovered = tail.length >= cfg.recoverSamples && tail.every((r) => r <= cfg.unstableRttMs);
    if (recovered)
        return null;
    const poorCount = window.filter((r) => r > cfg.poorRttMs).length;
    const unstableCount = window.filter((r) => r > cfg.unstableRttMs).length;
    if (poorCount >= cfg.degradeSamples)
        return 'poor';
    if (unstableCount >= cfg.degradeSamples)
        return 'unstable';
    return current;
}
export function useConnectionMonitor({ enabled, sendMessage, windowSeconds = 240, 
// Detection latency for a silent drop is heartbeatMs * (missLimit + 1).
// 1500 * (2 + 1) → the "Connection lost" modal appears within ~4.5s instead
// of ~30s. Env-overridable (REACT_APP_HEARTBEAT_*) so it can be tuned without
// a code change; brief blips still self-heal on the next pong.
// Detection window = heartbeatMs * (missLimit + 1) = 2000 * 5 = 10s. Sized to
// comfortably exceed a normal slow AI turn: the backend's assistant-turn
// classifier (now bounded at ~6s) is awaited inline on the WS loop and delays
// the `pong`, so a tighter window produced false "connection lost" on every
// slow turn. A real candidate drop persists well past 10s, so detection of a
// genuine drop is still timely. Env-overridable.
heartbeatMs = Number(process.env.REACT_APP_HEARTBEAT_INTERVAL_MS) || 2000, missLimit = Number(process.env.REACT_APP_HEARTBEAT_MISS_LIMIT) || 4, 
// Hard threshold (default 15 → 15 * 2000ms ≈ 30s of total backend silence)
// for forcing "connection lost" even while openAiOpen is a stale OPEN. Sized
// far above missLimit (4) so a slow classifier turn never reaches it; only a
// genuine, sustained drop does. Env-overridable.
hardMissLimit = Number(process.env.REACT_APP_HEARTBEAT_HARD_MISS_LIMIT) || 15, 
// RTT here is the ping→pong round-trip, which rides the BACKEND's processing
// latency (the consumer handles WS messages serially; the assistant-turn
// classifier, bounded ~6s, delays the pong) — it is NOT pure network latency.
// So the old 500/1500ms thresholds flagged "unstable/poor" on a healthy fast
// connection every time the backend was briefly busy. Raised well above the
// classifier bound so only genuinely slow round-trips grade down. Env-overridable.
unstableRttMs = Number(process.env.REACT_APP_RTT_UNSTABLE_MS) || 3000, poorRttMs = Number(process.env.REACT_APP_RTT_POOR_MS) || 8000, 
// When the monitor first arms, the backend may still be settling (finishing
// OpenAI session setup, draining buffered video) so its serial WS loop lags
// the first pongs. Give the same kind of grace as post-recovery so a slow
// startup doesn't immediately declare "connection lost". Env-overridable.
startupGraceMs = Number(process.env.REACT_APP_STARTUP_GRACE_MS) || 8000, getLinkHealth, isTurnActive, 
// WS-1: 3-of-5 to degrade, 2 consecutive fast samples to recover. Sized so a
// single slow AI turn (which stalls one pong) can never flip the pill, but a
// sustained real degradation surfaces within a few samples. Env-overridable.
rttWindowSize = Number(process.env.REACT_APP_RTT_WINDOW_SIZE) || 5, rttDegradeSamples = Number(process.env.REACT_APP_RTT_DEGRADE_SAMPLES) || 3, rttRecoverSamples = Number(process.env.REACT_APP_RTT_RECOVER_SAMPLES) || 2, }) {
    // When on, "connection lost" requires the realtime link to actually be down —
    // a healthy OpenAI WS suppresses the blocking overlay (soft pill instead).
    const requireSocketDown = isConnectionLostRequiresSocketDownEnabled();
    // When on, a genuine-loss signal (browser offline / hard heartbeat miss) may
    // override the readyState-gated suppression above. See the flag's docstring.
    const offlineOverrideEnabled = isConnectionLostOnBrowserOfflineEnabled();
    // When on, a HARD heartbeat-miss does NOT force the overlay while BOTH the
    // backend and OpenAI sockets are genuinely OPEN — that's poor connection, not a
    // loss. See the flag's docstring.
    const hardMissRequiresSocketDown = isHardMissRequiresSocketDownEnabled();
    // WS-2: when on, the blocking "connection lost" path stays disarmed until the
    // OpenAI link has been OPEN once, so the backend's slow join-time session build
    // can't flash the overlay before the interview has really started.
    const joinArmAfterAiEnabled = isJoinMonitorArmAfterAiEnabled();
    // WS-1: when on, grade the quality pill from a windowed RTT with hysteresis
    // instead of a single-sample flip.
    const netqSmoothingEnabled = isNetqSmoothingEnabled();
    const [connectionStatus, dispatch] = useReducer(connectionReducer, 'ACTIVE');
    const [connectionQuality, setQuality] = useState(null);
    const [reconnectCountdown, setReconnectCountdown] = useState(null);
    // A brief browser `offline` blip that recovers within this window must NOT
    // throw the full "connection lost" popup — debounce it and confirm with a
    // probe ping. Raised from 2s → 5s: the browser fires `offline` on sub-second
    // Wi-Fi fluctuations that still round-trip fine, and a 2s window popped the
    // full blocking modal on those. With 5s, a fluctuation that recovers (its pong
    // cancels the debounce) no longer surfaces the popup. Env-overridable.
    const offlineEventDebounceMs = Number(process.env.REACT_APP_OFFLINE_DEBOUNCE_MS) || 5000;
    // After recovering, suppress re-declaring offline for this grace period: right
    // after a reconnect the backend is busy (re-creating the session, draining
    // buffered video chunks) and its pongs lag, which would otherwise immediately
    // re-pop the popup even though the network is fine. Env-overridable.
    const recoveryGraceMs = Number(process.env.REACT_APP_RECOVERY_GRACE_MS) || 8000;
    const statusRef = useRef('ACTIVE');
    statusRef.current = connectionStatus;
    // WS-1: rolling window of recent (turn-idle) RTT samples + the current graded
    // quality, both read inside notifyPong which is a stable callback.
    const rttWindowRef = useRef([]);
    const qualityRef = useRef(null);
    qualityRef.current = connectionQuality;
    const unansweredPingsRef = useRef(0);
    // WS-2: flips true the first time the OpenAI WS is observed OPEN. Until then a
    // non-forced goOffline is suppressed (the join-time session build hasn't
    // finished, so a "connection lost" would be a false positive over the boot).
    const openAiEverOpenRef = useRef(false);
    const lastPingAtRef = useRef(null);
    const offlineSinceRef = useRef(null);
    const countdownTimerRef = useRef(null);
    // Pending "declare offline" timer armed by the browser `offline` event;
    // cancelled if a pong arrives within offlineEventDebounceMs.
    const offlineDebounceTimerRef = useRef(null);
    // Wall-clock until which the heartbeat must not re-declare offline (set on recovery).
    const recoveryGraceUntilRef = useRef(0);
    const clearCountdown = useCallback(() => {
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
        setReconnectCountdown(null);
    }, []);
    const goOffline = useCallback((opts) => {
        var _a, _b, _c;
        if (statusRef.current === 'RECONNECTING' || statusRef.current === 'INTERRUPTED')
            return;
        const reason = (_a = opts === null || opts === void 0 ? void 0 : opts.reason) !== null && _a !== void 0 ? _a : 'unknown';
        // Don't declare "connection lost" while the realtime link is actually alive.
        // The audio path is browser↔OpenAI directly: if that socket is OPEN the
        // candidate can still converse, so a lagging backend pong / spurious browser
        // `offline` blip must NOT throw the blocking overlay — surface only the soft
        // "unstable" pill and keep waiting for a real drop or recovery.
        //
        // EXCEPTION (genuine-loss override): `openAiOpen` is `readyState === OPEN`,
        // a STALE local flag — on a real internet drop the socket stays OPEN for
        // many seconds. So a genuine-loss signal (`force`: the browser reports
        // offline, or a hard heartbeat-miss) must NOT be suppressed by it, else the
        // popup never shows and the timer never freezes on a truly-dead link.
        const browserOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
        const force = Boolean(opts === null || opts === void 0 ? void 0 : opts.force) || (offlineOverrideEnabled && browserOffline);
        // WS-2: keep the ever-open latch current, then suppress the blocking overlay
        // until the OpenAI link has been OPEN at least once. During the join-time
        // session build (openAiOpen still false) a lagging startup pong or a
        // spurious `offline` blip would otherwise flash "connection lost" over the
        // boot screen. A genuine-loss signal (`force`: browser offline or hard
        // heartbeat-miss) is exempt, so a real outage at join still surfaces.
        if (getLinkHealth === null || getLinkHealth === void 0 ? void 0 : getLinkHealth().openAiOpen)
            openAiEverOpenRef.current = true;
        if (!force && joinArmAfterAiEnabled && getLinkHealth && !openAiEverOpenRef.current) {
            setQuality('unstable');
            if (statusRef.current === 'ACTIVE')
                dispatch('DEGRADED_SIGNAL');
            // eslint-disable-next-line no-console
            console.info('[connection-monitor] offline suppressed (OpenAI link not yet opened; join-time) reason=%s', reason);
            return;
        }
        if (!force && requireSocketDown && getLinkHealth) {
            const { openAiOpen } = getLinkHealth();
            if (openAiOpen) {
                setQuality('unstable');
                if (statusRef.current === 'ACTIVE')
                    dispatch('DEGRADED_SIGNAL');
                // eslint-disable-next-line no-console
                console.info('[connection-monitor] offline suppressed (openAiOpen, no genuine-loss signal) reason=%s', reason);
                return;
            }
        }
        const linkHealth = getLinkHealth === null || getLinkHealth === void 0 ? void 0 : getLinkHealth();
        // eslint-disable-next-line no-console
        console.warn('[connection-monitor] declaring OFFLINE reason=%s force=%s browserOffline=%s backendOpen=%s openAiOpen=%s', reason, force, browserOffline, (_b = linkHealth === null || linkHealth === void 0 ? void 0 : linkHealth.backendOpen) !== null && _b !== void 0 ? _b : 'n/a', (_c = linkHealth === null || linkHealth === void 0 ? void 0 : linkHealth.openAiOpen) !== null && _c !== void 0 ? _c : 'n/a');
        dispatch('OFFLINE');
        setQuality(null);
        sendMessage({ type: 'interview_paused' });
        offlineSinceRef.current = Date.now();
        setReconnectCountdown(windowSeconds);
        // NOTE: the per-second countdown is ticked by a dedicated effect keyed on
        // `connectionStatus === 'RECONNECTING'` (below) — NOT by a setInterval
        // created here. Creating it here was fragile: the heartbeat effect's
        // cleanup cleared `countdownTimerRef` on every re-render, and because the
        // call site passes an inline `getLinkHealth` (new ref each render) that
        // effect re-ran constantly — so the countdown interval was torn down before
        // it could tick and the popup froze at its initial value ("4:00").
    }, [
        sendMessage,
        windowSeconds,
        requireSocketDown,
        getLinkHealth,
        offlineOverrideEnabled,
        joinArmAfterAiEnabled,
    ]);
    // Tick the reconnect-window countdown while RECONNECTING. Owning the interval
    // in its own effect (keyed on the status) makes it immune to unrelated
    // re-renders/cleanups — fixing the "popup timer stuck at 4:00" bug.
    useEffect(() => {
        if (connectionStatus !== 'RECONNECTING')
            return;
        if (offlineSinceRef.current == null)
            offlineSinceRef.current = Date.now();
        const tick = () => {
            var _a;
            const elapsed = (Date.now() - ((_a = offlineSinceRef.current) !== null && _a !== void 0 ? _a : Date.now())) / 1000;
            const remaining = getReconnectCountdown(windowSeconds, elapsed);
            setReconnectCountdown(remaining);
            if (remaining <= 0)
                dispatch('WINDOW_EXPIRED');
        };
        tick(); // reflect elapsed immediately, don't wait a full second
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [connectionStatus, windowSeconds]);
    const setDegraded = useCallback((quality) => {
        setQuality(quality);
        if (statusRef.current === 'ACTIVE')
            dispatch('DEGRADED_SIGNAL');
    }, []);
    // A real pong proves reachability; its MEASURED round-trip grades quality.
    const notifyPong = useCallback(() => {
        var _a;
        unansweredPingsRef.current = 0;
        // A real pong proves we're connected — cancel any pending offline-from-event
        // (the `offline` blip recovered before the debounce elapsed).
        if (offlineDebounceTimerRef.current) {
            clearTimeout(offlineDebounceTimerRef.current);
            offlineDebounceTimerRef.current = null;
        }
        const sentAt = lastPingAtRef.current;
        const rtt = sentAt != null ? Date.now() - sentAt : 0;
        const s = statusRef.current;
        if (s === 'RECONNECTING') {
            dispatch('RECOVERED');
            sendMessage({ type: 'interview_resume_request' });
            clearCountdown();
            offlineSinceRef.current = null;
            setQuality(null);
            rttWindowRef.current = []; // WS-1: drop stale pre-drop samples
            dispatch('VERIFIED');
            // Give the just-reconnected (still-settling) backend a grace period before
            // the heartbeat is allowed to re-declare offline — otherwise its lagging
            // pongs re-pop the popup right after recovery.
            recoveryGraceUntilRef.current = Date.now() + recoveryGraceMs;
        }
        else if (!netqSmoothingEnabled) {
            // Legacy single-sample grading (flag off).
            if (rtt > poorRttMs) {
                setDegraded('poor');
            }
            else if (rtt > unstableRttMs) {
                setDegraded('unstable');
            }
            else {
                setQuality(null);
                if (s === 'DEGRADED')
                    dispatch('RECOVERED');
            }
        }
        else if (isTurnActive === null || isTurnActive === void 0 ? void 0 : isTurnActive()) {
            // WS-1: this RTT rides the backend's assistant-turn classifier (~6s), not
            // the network. Don't let it grade quality — skip the sample entirely and
            // hold the current pill. Reachability was already proven above.
            // eslint-disable-next-line no-console
            console.info('[connection-monitor] RTT sample skipped (assistant turn active) rtt=%dms', rtt);
        }
        else {
            // WS-1: windowed grading with hysteresis — one slow pong can no longer
            // flip the pill; a sustained degradation still surfaces within a few
            // samples, and recovery needs a few consecutive fast samples.
            const w = rttWindowRef.current;
            w.push(rtt);
            if (w.length > rttWindowSize)
                w.shift();
            const next = gradeQualityFromWindow(w, qualityRef.current, {
                unstableRttMs,
                poorRttMs,
                degradeSamples: rttDegradeSamples,
                recoverSamples: rttRecoverSamples,
            });
            if (next !== qualityRef.current) {
                // eslint-disable-next-line no-console
                console.info('[netq] quality %s→%s rtts=%o', (_a = qualityRef.current) !== null && _a !== void 0 ? _a : 'stable', next !== null && next !== void 0 ? next : 'stable', w.slice());
            }
            if (next === null) {
                // Only on a genuine recovery transition (was degraded → now stable) do we
                // clear the window, so aged-out slow samples can't re-trip the degrade
                // counts on the very next sample. While already stable we KEEP the window
                // so it can accumulate toward a real degradation.
                if (qualityRef.current !== null)
                    rttWindowRef.current = [];
                setQuality(null);
                if (s === 'DEGRADED')
                    dispatch('RECOVERED');
            }
            else {
                setDegraded(next);
            }
        }
    }, [
        sendMessage,
        clearCountdown,
        setDegraded,
        poorRttMs,
        unstableRttMs,
        recoveryGraceMs,
        netqSmoothingEnabled,
        isTurnActive,
        rttWindowSize,
        rttDegradeSamples,
        rttRecoverSamples,
    ]);
    // The transport is back (backend WS onopen). Recover immediately — mirrors the
    // RECONNECTING branch of notifyPong, but driven by the socket reopening rather
    // than by waiting for the next pong, so the countdown can't tip into
    // INTERRUPTED while a successful reconnect is still settling its first pong.
    const notifyReconnected = useCallback(() => {
        unansweredPingsRef.current = 0;
        if (offlineDebounceTimerRef.current) {
            clearTimeout(offlineDebounceTimerRef.current);
            offlineDebounceTimerRef.current = null;
        }
        if (statusRef.current !== 'RECONNECTING')
            return;
        dispatch('RECOVERED');
        sendMessage({ type: 'interview_resume_request' });
        clearCountdown();
        offlineSinceRef.current = null;
        setQuality(null);
        // NR-4: drop stale pre-drop RTT samples (mirrors the notifyPong RECONNECTING
        // branch). Without this, a slow sample from before the outage lingered in the
        // window and immediately re-degraded the pill right after a clean reconnect —
        // a visible residual "poor/unstable" flash on recovery.
        rttWindowRef.current = [];
        dispatch('VERIFIED');
        recoveryGraceUntilRef.current = Date.now() + recoveryGraceMs;
    }, [sendMessage, clearCountdown, recoveryGraceMs]);
    useEffect(() => {
        if (!enabled)
            return;
        // Startup grace: the backend may still be settling when the monitor first
        // arms, so hold off declaring offline for a short window (mirrors the
        // post-recovery grace). A genuine drop is still caught once it elapses.
        recoveryGraceUntilRef.current = Date.now() + startupGraceMs;
        // `offline` is a fast NEGATIVE hint, but it can fire on a sub-second blip
        // that immediately recovers. Debounce it: probe with a ping now, and only
        // declare lost after offlineEventDebounceMs if NO pong has round-tripped in
        // the meantime — a real pong cancels this timer via notifyPong. The timer
        // surviving the full (now 5s) window is itself the corroboration that the
        // link is genuinely down, so a brief fluctuation the browser reports as
        // `offline` but which still round-trips never throws the popup.
        const onOffline = () => {
            if (statusRef.current === 'RECONNECTING' || statusRef.current === 'INTERRUPTED')
                return;
            lastPingAtRef.current = Date.now();
            sendMessage({ type: 'ping' });
            if (offlineDebounceTimerRef.current)
                clearTimeout(offlineDebounceTimerRef.current);
            offlineDebounceTimerRef.current = setTimeout(() => {
                offlineDebounceTimerRef.current = null;
                // Genuine browser-offline: if navigator is still offline when the
                // debounce elapses, goOffline() forces the overlay past the stale
                // openAiOpen guard (see goOffline). A recovered blip already cancelled
                // this timer via notifyPong.
                goOffline({ reason: 'browser_offline_event' });
            }, offlineEventDebounceMs);
        };
        const onOnline = () => sendMessage({ type: 'ping' }); // probe; never resume on this
        window.addEventListener('offline', onOffline);
        window.addEventListener('online', onOnline);
        const heartbeat = setInterval(() => {
            lastPingAtRef.current = Date.now();
            unansweredPingsRef.current += 1;
            sendMessage({ type: 'ping' });
            // WS-2: latch "OpenAI has been open once" continuously while it is OPEN, so
            // that a later genuine mid-interview drop (openAiOpen false at goOffline
            // time) is NOT mistaken for the join-time not-yet-opened case and correctly
            // surfaces the overlay.
            if (getLinkHealth === null || getLinkHealth === void 0 ? void 0 : getLinkHealth().openAiOpen)
                openAiEverOpenRef.current = true;
            const unanswered = unansweredPingsRef.current;
            if (unanswered > hardMissLimit) {
                // HARD miss: total backend silence far beyond any slow-turn explanation.
                // Force the overlay even if openAiOpen is a stale OPEN — this catches a
                // genuine drop where the browser never fired `offline` (e.g. upstream
                // ISP outage with the local NIC still "connected", so navigator.onLine
                // lies). Still respects the post-recovery / startup grace.
                if (Date.now() >= recoveryGraceUntilRef.current) {
                    // But if BOTH sockets are genuinely OPEN, all three legs
                    // (browser↔backend, browser↔OpenAI) are alive — the candidate can
                    // still reach the server and talk to the AI, so a run of missed pongs
                    // is only POOR connection, never a loss. Do NOT throw the blocking
                    // overlay; grade "poor" and keep waiting for a real socket-down (or a
                    // browser-offline event) to force it. This kills the false positive
                    // seen on a backgrounded/throttled tab whose heartbeat stalled while
                    // both sockets stayed OPEN.
                    const lh = getLinkHealth === null || getLinkHealth === void 0 ? void 0 : getLinkHealth();
                    const bothSocketsOpen = Boolean((lh === null || lh === void 0 ? void 0 : lh.backendOpen) && (lh === null || lh === void 0 ? void 0 : lh.openAiOpen));
                    if (hardMissRequiresSocketDown && getLinkHealth && bothSocketsOpen) {
                        setDegraded('poor');
                        // eslint-disable-next-line no-console
                        console.info('[connection-monitor] hard miss but both sockets OPEN — grading POOR, not offline (unanswered=%d)', unanswered);
                    }
                    else {
                        goOffline({ force: true, reason: 'heartbeat_hard_miss' });
                    }
                }
            }
            else if (unanswered > missLimit) {
                // Post-recovery grace: a just-reconnected backend's lagging pongs must
                // not immediately re-declare offline. A genuine re-drop is still caught
                // once the grace elapses.
                if (Date.now() >= recoveryGraceUntilRef.current) {
                    // NR-1: do NOT throw the blocking "reconnecting" overlay on a soft miss
                    // when the connection is demonstrably alive. Two cases the old code
                    // wrongly treated as a loss:
                    //   • BOTH sockets still OPEN → the candidate can reach the server and
                    //     the AI; a run of missed pongs is only POOR connection.
                    //   • an AI turn is in flight → the `pong` is legitimately queued
                    //     behind the turn on the backend's serial WS loop, so the miss is
                    //     expected, not a drop.
                    // A genuine drop is still caught by the hard-miss (socket-down),
                    // browser-offline, and socket-close paths. This mirrors the hard-miss
                    // both-sockets-open protection above.
                    const lh = getLinkHealth === null || getLinkHealth === void 0 ? void 0 : getLinkHealth();
                    const bothSocketsOpen = Boolean((lh === null || lh === void 0 ? void 0 : lh.backendOpen) && (lh === null || lh === void 0 ? void 0 : lh.openAiOpen));
                    const turnActive = Boolean(isTurnActive === null || isTurnActive === void 0 ? void 0 : isTurnActive());
                    if (bothSocketsOpen || turnActive) {
                        setDegraded('poor');
                        // eslint-disable-next-line no-console
                        console.info('[connection-monitor] soft miss but link alive (bothOpen=%s turnActive=%s) — grading POOR, not offline (unanswered=%d)', bothSocketsOpen, turnActive, unanswered);
                    }
                    else {
                        goOffline({ reason: 'heartbeat_miss' });
                    }
                }
            }
            else if (unanswered > 2 && !(isTurnActive === null || isTurnActive === void 0 ? void 0 : isTurnActive())) {
                // NR-2: flag "unstable" after several missed pings — but NOT while an AI
                // turn is in flight. The backend handles WS messages serially, so a turn
                // legitimately stalls the pong for several seconds; flipping the pill to
                // "unstable" on every turn (then recovering on the next pong) was a core
                // source of the flapping. Genuine sustained slowness is still surfaced by
                // the windowed RTT grading (gradeQualityFromWindow) once the turn ends.
                setDegraded('unstable');
            }
        }, heartbeatMs);
        return () => {
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('online', onOnline);
            clearInterval(heartbeat);
            if (countdownTimerRef.current)
                clearInterval(countdownTimerRef.current);
            if (offlineDebounceTimerRef.current)
                clearTimeout(offlineDebounceTimerRef.current);
        };
    }, [
        enabled,
        goOffline,
        setDegraded,
        sendMessage,
        heartbeatMs,
        missLimit,
        hardMissLimit,
        offlineEventDebounceMs,
        startupGraceMs,
        getLinkHealth,
        isTurnActive,
        hardMissRequiresSocketDown,
    ]);
    return {
        connectionStatus,
        connectionQuality,
        reconnectCountdown,
        notifyPong,
        notifyReconnected,
    };
}
